"use client";

import { ChevronRight, HardDrive, RefreshCw, Users } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shadcn-components/ui/card";
import { Badge } from "@/shadcn-components/ui/badge";
import { Separator } from "@/shadcn-components/ui/separator";
import { formatBytes } from "@/lib/storage/calculate";
import type { StorageInventory } from "@/lib/storage/inventory";

interface StorageHomeViewProps {
  inventory: StorageInventory;
  activeAccountId: string | null;
  loading: boolean;
  refreshing?: boolean;
  lastScannedAt?: number | null;
  onRefresh: () => void;
  onOpenApp: () => void;
  onOpenAccount: (accountId: string) => void;
  onClearApp: () => void;
  onClearAll: () => void;
  onClearOrphan: () => void;
}

function formatScannedAt(ts: number | null | undefined): string | null {
  if (ts == null) return null;
  const diff = Date.now() - ts;
  if (diff < 60_000) return "Updated just now";
  if (diff < 3600_000) return `Updated ${Math.floor(diff / 60_000)}m ago`;
  return `Updated ${new Date(ts).toLocaleString()}`;
}

export function StorageHomeView({
  inventory,
  activeAccountId,
  loading,
  refreshing = false,
  lastScannedAt,
  onRefresh,
  onOpenApp,
  onOpenAccount,
  onClearApp,
  onClearAll,
  onClearOrphan,
}: StorageHomeViewProps) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <HardDrive className="h-5 w-5" />
            Storage overview
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={onRefresh}
            disabled={loading || refreshing}
            aria-label="Refresh storage"
          >
            <RefreshCw
              className={`h-4 w-4 ${loading || refreshing ? "animate-spin" : ""}`}
            />
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {(refreshing || formatScannedAt(lastScannedAt)) && (
            <p className="text-xs text-muted-foreground">
              {refreshing
                ? "Refreshing sizes…"
                : formatScannedAt(lastScannedAt)}
            </p>
          )}
          <div className="flex items-center justify-between font-medium">
            <span>Total cache</span>
            <span className="font-mono">{formatBytes(inventory.totalBytes)}</span>
          </div>
          <Separator />
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">App cache</span>
            <span className="font-mono text-muted-foreground">
              {formatBytes(inventory.app.totalBytes)}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Account caches</span>
            <span className="font-mono text-muted-foreground">
              {formatBytes(
                inventory.accounts.reduce((s, a) => s + a.totalBytes, 0),
              )}
            </span>
          </div>
        </CardContent>
      </Card>

      <div>
        <h3 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
          APP CACHE
        </h3>
        <Card>
          <CardContent className="p-0">
            <Button
              variant="ghost"
              onClick={onOpenApp}
              className="w-full h-auto p-3 justify-between hover:bg-muted/50 rounded-lg"
            >
              <span className="text-sm font-medium">App data & preferences</span>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="font-mono text-xs">
                  {formatBytes(inventory.app.totalBytes)}
                </Badge>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </Button>
            <Separator />
            <Button
              variant="ghost"
              onClick={onClearApp}
              className="w-full h-auto p-3 justify-between hover:bg-muted/50 rounded-lg text-destructive hover:text-destructive"
            >
              <span className="text-sm">Clear app cache</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>

      <div>
        <h3 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
          ACCOUNT CACHE
        </h3>
        <Card>
          <CardContent className="p-0">
            {inventory.accounts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No accounts configured</p>
              </div>
            ) : (
              inventory.accounts.map((row, i) => {
                const isActive = row.accountId === activeAccountId;
                return (
                  <div key={row.accountId}>
                    {i > 0 && <Separator />}
                    <Button
                      variant="ghost"
                      onClick={() => onOpenAccount(row.accountId)}
                      className="w-full h-auto p-3 justify-between hover:bg-muted/50 rounded-none"
                    >
                      <div className="text-left">
                        <p className="text-sm font-medium">{row.label}</p>
                        {isActive && (
                          <Badge variant="default" className="text-xs mt-1">
                            Active
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="font-mono text-xs">
                          {formatBytes(row.totalBytes)}
                        </Badge>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </Button>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>

      {inventory.orphan && inventory.orphan.bytes > 0 && (
        <div>
          <h3 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
            OTHER
          </h3>
          <Card>
            <CardContent className="p-0">
              <Button
                variant="ghost"
                onClick={onClearOrphan}
                className="w-full h-auto p-3 justify-between hover:bg-muted/50 rounded-lg"
              >
                <span className="text-sm font-medium">{inventory.orphan.label}</span>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="font-mono text-xs">
                    {formatBytes(inventory.orphan.bytes)}
                  </Badge>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      <div>
        <h3 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
          ACTIONS
        </h3>
        <Card>
          <CardContent className="p-0">
            <Button
              variant="ghost"
              onClick={onClearAll}
              className="w-full h-auto p-3 justify-between hover:bg-muted/50 rounded-lg text-destructive hover:text-destructive"
            >
              <span className="text-sm font-medium">Clear all caches</span>
              <Badge variant="outline" className="font-mono text-xs">
                {formatBytes(inventory.totalBytes)}
              </Badge>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
